import { ArrowLeft, ExternalLink, Loader2, MapPin, Trash2 } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
  formatCitation,
  KNOWLEDGE_SOURCE_LABELS,
  type BBox,
  type CitationLocator,
  type KnowledgeEntry,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { DecisionDetail } from "./decision-dialog";
import { useDecisions } from "./decisions-api";
import { useProjectContext } from "@/app/project-context";
import { AuthedImage } from "@/components/gb/authed-file";
import { MarkdownView } from "@/components/gb/markdown-view";
import { Button } from "@/components/ui/button";
import { linkFor } from "@/features/chat/chat-links";
import { photoContentPath } from "@/features/journal/journal-photos-api";
import { cn } from "@/lib/utils";
import { previewPath, useBrainDocument, useBrainEntry, useDeleteBrainEntry } from "./brain-api";
import { cameFrom, type CameFrom } from "./file-link";
import { FilePreview } from "./file-preview";
import { SOURCE_STYLES } from "./source-display";

/** One knowledge-base entry, with its full body. */
export function BrainEntryScreen() {
  const { entryId } = useParams();
  const { projectId } = useProjectContext();
  const navigate = useNavigate();
  const location = useLocation();
  // An answer's source card says it opened this, so the back link returns to
  // the answer. `/brain` redirects to Ask, which is not where the person was.
  const from: CameFrom = cameFrom(location.state) ?? {
    path: `/projects/${projectId}/brain`,
    label: "Back to the Brain",
  };
  const { data: entry, isPending, isError, error, refetch } = useBrainEntry(projectId, entryId);
  const remove = useDeleteBrainEntry(projectId);

  if (isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Loading the entry…</span>
        </div>
      </ContentColumn>
    );
  }

  if (isError) {
    return (
      <ContentColumn>
        <BackLink from={from} />
        <div className="mt-4 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">Couldn’t load this entry.</p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">
            {error instanceof Error ? error.message : "Something went wrong."}
          </p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </ContentColumn>
    );
  }

  const where = placeOf(entry.locator);
  // A passage of a document reads first, then the file at its page. A
  // photograph is shown first, because its text is written about the picture.
  const fileFirst = entry.sourceType === "photo";

  return (
    <ContentColumn>
      <BackLink from={from} />

      <header className="mt-4 mb-5">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-[3px] font-display text-[0.66rem] font-semibold tracking-[0.06em] uppercase",
              SOURCE_STYLES[entry.sourceType],
            )}
          >
            {KNOWLEDGE_SOURCE_LABELS[entry.sourceType]}
          </span>
          {/* The where-block below says the same, in parts. */}
          {entry.citation && !where && (
            <span className="text-[0.82rem] text-muted">{entry.citation}</span>
          )}
        </div>
        <h1 className="font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
          {entry.title}
        </h1>
      </header>

      {where && <WhereInFile where={where} documentId={entry.documentId} projectId={projectId} />}

      {fileFirst && <EntryFile entry={entry} projectId={projectId} where={where} />}

      {/* A decision is more than its prose. The entry's body holds the
          statement and rationale, but what was *asked* and what it rests on
          live on the decision row — so this screen showed neither until it
          rendered the same detail the Decisions tab does. */}
      {entry.sourceType === "decision" && entry.sourceRef ? (
        <DecisionEntry projectId={projectId} decisionId={entry.sourceRef} body={entry.body} />
      ) : (
      <article className="rounded-card-lg border-[1.5px] border-border bg-surface p-6">
        {/* A chunk of a document is markdown: the parsers write its headings
            and tables that way. The same viewer as the file's pages, and
            nothing in it is a live link, because somebody else wrote it. */}
        {entry.body.trim() ? (
          <EntryText text={entry.body} />
        ) : (
          <EmptyBody sourceType={entry.sourceType} />
        )}
      </article>
      )}

      {!fileFirst && <EntryFile entry={entry} projectId={projectId} where={where} />}

      {entry.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {entry.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border bg-canvas px-2.5 py-[3px] text-[0.75rem] font-semibold text-ink-slate"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {entry.documentId && !where && (
          // The viewer, not a download. This screen only sees a `project-doc`
          // row through a direct link now — the Brain list folds them into
          // their file — but when it does, the useful destination is the file
          // open at its pages rather than a copy in the downloads folder.
          <Button asChild variant="secondary" size="sm">
            <Link to={`/projects/${projectId}/brain/files/${entry.documentId}`}>
              <ExternalLink size={15} />
              Open the document
            </Link>
          </Button>
        )}
        {entry.sourceType === "upload" && (
          <Button
            variant="secondary"
            size="sm"
            disabled={remove.isPending}
            onClick={async () => {
              await remove.mutateAsync(entry.id);
              void navigate(`/projects/${projectId}/brain`);
            }}
          >
            <Trash2 size={15} />
            {remove.isPending ? "Deleting…" : "Delete note"}
          </Button>
        )}
      </div>

      {remove.error && (
        <p role="alert" className="mt-3 text-[0.85rem] font-semibold text-danger">
          {remove.error.message}
        </p>
      )}
    </ContentColumn>
  );
}

/**
 * The thing the entry is *about*, shown rather than named.
 *
 * A Brain entry is text written from a file — a photograph's description, a
 * chunk of a manual. Until now this screen rendered only that text, so a
 * photograph whose description was empty was a filename and a blank card, and
 * the picture it was written from was one route away with nothing pointing at
 * it. The entry has always known which file it came from; it just never showed
 * it.
 *
 * Photographs resolve directly, because a photo entry carries its day and its
 * `sourceRef` is the photograph's id. A document needs one more request: what
 * the viewer may show is `previewType`, which lives on the document row and not
 * on the entry.
 */
function EntryFile({
  entry,
  projectId,
  where,
}: {
  entry: KnowledgeEntry;
  projectId: string;
  where: Place | null;
}) {
  if (entry.sourceType === "photo" && entry.journalEntryId && entry.sourceRef) {
    return (
      <figure className="mb-5 overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface-sunken">
        <AuthedImage
          path={photoContentPath(projectId, entry.journalEntryId, entry.sourceRef)}
          alt={entry.title}
          className="max-h-[62vh] w-full object-contain"
          fallbackClassName="h-[280px]"
        />
      </figure>
    );
  }

  if (entry.documentId) {
    return <EntryDocumentFile entry={entry} projectId={projectId} where={where} />;
  }

  return null;
}

/** A document's own pages, for an entry that is a chunk of one, open at its page. */
function EntryDocumentFile({
  entry,
  projectId,
  where,
}: {
  entry: KnowledgeEntry;
  projectId: string;
  where: Place | null;
}) {
  const { data: document } = useBrainDocument(projectId, entry.documentId ?? undefined);

  // No panel at all while it loads, and none for a format nothing can show.
  // A placeholder box would take the same space as the picture and then admit
  // there is nothing in it, which is worse than the text simply starting here.
  if (!document?.previewType) return null;

  const picture = document.previewType.startsWith("image/");
  return (
    <figure className="mt-5 overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface-sunken">
      <FilePreview
        path={previewPath(projectId, document.id)}
        type={document.previewType}
        filename={document.filename}
        page={where?.page}
        // Drawn on a picture file only. On a PDF the viewer draws it, on a
        // picture of the page, behind "Open at page".
        box={picture ? where?.box : null}
        className="h-[62vh] w-full border-0 bg-white"
      />
    </figure>
  );
}

/**
 * Where an entry's text is in its file, as the parts this screen uses.
 *
 * Null when the locator knows only the file: the title already says that.
 */
type Place = {
  locator: Exclude<CitationLocator, { kind: "document" }>;
  page: number | null;
  box: BBox | null;
};

function placeOf(locator: CitationLocator | null): Place | null {
  if (!locator || locator.kind === "document") return null;
  if (locator.kind === "sheet-range") return { locator, page: null, box: null };
  return { locator, page: locator.page ?? null, box: locator.bbox ?? null };
}

/**
 * Where the passage is in its file, said before the passage.
 *
 * A citation opens this screen, and the next thing a person needs is where to
 * look in the file. The line is `formatCitation`'s, so it says what the
 * answer's source card says. The snippet is a line of the passage as written,
 * for the file's own find box: a DOCX page number can drift by a page, and the
 * snippet finds the passage anyway (`CitationLocator`).
 */
function WhereInFile({
  where,
  documentId,
  projectId,
}: {
  where: Place;
  documentId: string | null;
  projectId: string;
}) {
  const { locator, page, box } = where;
  const snippet = locator.kind === "page" ? locator.snippet?.trim() : undefined;
  const columns = locator.kind === "sheet-range" ? locator.columns : undefined;
  const open = documentId
    ? linkFor(projectId, {
        kind: "document",
        documentId,
        ...(page ? { page } : {}),
        ...(box ? { box } : {}),
      })
    : null;

  return (
    <section
      aria-labelledby="where-in-file"
      className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-3 rounded-card-lg border-[1.5px] border-border bg-surface-sunken px-5 py-4"
    >
      <div className="min-w-0 flex-1">
        <h2
          id="where-in-file"
          className="flex items-center gap-1.5 font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase"
        >
          <MapPin size={13} aria-hidden />
          Where it is in the file
        </h2>
        <p className="mt-1.5 text-[0.95rem] font-semibold leading-snug text-ink first-letter:uppercase">
          {formatCitation(locator)}
        </p>
        {columns && columns.length > 0 && (
          <p className="mt-1 text-[0.82rem] text-ink-slate">Columns: {columns.join(", ")}</p>
        )}
        {snippet && (
          <p className="mt-2 text-[0.82rem] leading-[1.5] text-ink-slate">
            To find it in the file, search for{" "}
            <q className="font-semibold text-ink select-all">{snippet}</q>
          </p>
        )}
      </div>
      {open && (
        <Button asChild variant="secondary" size="sm" className="shrink-0">
          <Link to={open.href}>
            <ExternalLink size={15} />
            {page ? `Open at page ${page}` : "Open the file"}
          </Link>
        </Button>
      )}
    </section>
  );
}

/** An entry's text, rendered as the file's pages are. */
function EntryText({ text }: { text: string }) {
  return (
    <MarkdownView
      text={text.trim()}
      trust="document"
      className="text-[0.95rem] leading-[1.6] text-ink-prose"
    />
  );
}

/**
 * A body with nothing in it, said out loud.
 *
 * `"".split("\n\n")` is `[""]`, so an empty body used to render one empty
 * paragraph inside a bordered card — a blank box with no explanation, which is
 * exactly what a photograph gets in an environment with no vision model
 * configured. `document-ai/0004` asks for *failed to read* and *nothing to
 * read* to be distinguishable rather than sharing one empty state; this is the
 * entry screen finally keeping that promise.
 *
 * It deliberately does not claim the read failed. From here that is not
 * knowable — an uncaptioned photo nobody has described yet looks identical.
 */
function EmptyBody({ sourceType }: { sourceType: KnowledgeEntry["sourceType"] }) {
  return (
    <p className="text-[0.9rem] leading-[1.55] text-muted">
      {sourceType === "photo"
        ? "Nothing has been written about this photo yet — no caption, and no description from a model. It is filed and searchable by its filename and the day it was taken."
        : "This entry has no text. It is filed and searchable by its title."}
    </p>
  );
}

function BackLink({ from }: { from: CameFrom }) {
  return (
    <Link
      to={from.path}
      className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft size={15} />
      {from.label}
    </Link>
  );
}

/**
 * A decision, opened from the Brain rather than from the Decisions tab.
 *
 * Renders the same `DecisionDetail` that tab does, so the two cannot describe
 * the same decision differently. `body` is the fallback: a decision row that
 * cannot be found — deleted, or from a project the caller cannot read — still
 * has the entry's own prose, which is better than an empty panel.
 */
function DecisionEntry({
  projectId,
  decisionId,
  body,
}: {
  projectId: string;
  decisionId: string;
  body: string;
}) {
  const { data, isPending } = useDecisions(projectId);
  const decision = data?.decisions.find((candidate) => candidate.id === decisionId);

  if (isPending) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted" role="status">
        <Loader2 size={16} className="animate-spin" />
        <span className="text-[0.9rem]">Loading the decision…</span>
      </div>
    );
  }

  if (!decision) {
    return (
      <article className="rounded-card-lg border-[1.5px] border-border bg-surface p-6">
        <EntryText text={body} />
      </article>
    );
  }

  return (
    <article className="rounded-card-lg border-[1.5px] border-border bg-surface p-6">
      <DecisionDetail projectId={projectId} decision={decision} />
    </article>
  );
}
